import axios from 'axios';
import AppManager from '../app-manager';
import fragmentManager from '../fragment-manager';
import ProxyDocument, { ProxyHead } from './proxy-document';
import ProxySandbox from './proxySandbox';

const assetName = 'main';
const mainJsKey = `${assetName}.js`;
const mainCssKey = `${assetName}.css`;
const fragmentJsKey = 'fragment.js';
const fragmentCssKey = 'fragment.css';

const isolationProxyMap = new Map();
const manifestMap = new Map();
let globalVarsException = null;

const testIdProp = 'data-testid';

export const warnForGlobal = (globalVar, target) => {
  console.error(
    `You are not allowed to operate on global variable with property/attribute/function ${globalVar} ${target}. 
      Add exception on detail in you config.json if you intent to`,
  );
};

export const initialGlobalExceptionList = configJson => {
  if (globalVarsException) {
    console.error(
      'You can only initialize exception list in shell for once from your config.json or any other source. Not allowed change exception dynamically.',
    );
    return;
  }
  globalVarsException = {};
  configJson.components.forEach(mfe => {
    globalVarsException[mfe.name] = {
      ...(mfe?.config?.globalVarsException || {}),
      _mfe: mfe,
    };
  });
};

export const getValidName = microfrontendName => {
  // return microfrontendName;
  return (
    '__proxyFor' +
    microfrontendName
      .split('-')
      .map(part => part.slice(0, 1).toUpperCase() + part.slice(1))
      .join('')
  );
};

export const transformDynamicScriptTag = ({
  node,
  scriptContent,
  scriptMap,
  proxyWindowName,
  proxyDocumentName,
}) => {
  let dynamicNode = node || document.createElement('script');
  let scriptText =
    `;(function(window, self, globalThis, document){with(window){;` +
    `${scriptContent}}}).bind(window.${proxyWindowName})(window.${proxyWindowName}, window.${proxyWindowName}, ` +
    `window.${proxyWindowName}, window.${proxyDocumentName});${scriptMap}`;
  dynamicNode.src = null;
  dynamicNode.innerHTML = scriptText;
  return dynamicNode;
};

export const proxyAppendChild = (node, callback, target, proxyWindowName, proxyDocumentName) => {
  if (node.tagName === 'SCRIPT') {
    if (node.src && node.src !== '') {
      const originalSource = node.src;
      node['original-src'] = originalSource;
      delete node.src;
      axios.get(originalSource).then(result => {
        transformDynamicScriptTag({
          node,
          scriptContent: result.data,
          scriptMap: '',
          proxyWindowName,
          proxyDocumentName,
        });
      });
      return target.appendChild(node);
    } else {
      delete node.src;
      let scriptNode = transformDynamicScriptTag({
        node,
        scriptContent: node.innerHTML,
        scriptMap: '',
        proxyWindowName,
        proxyDocumentName,
      });

      return target.appendChild(scriptNode);
    }
  }
  return target.appendChild(node);
};

export const createProxyWindowAndDocument = ({
  microfrontendHost,
  microfrontendName,
  head,
  body,
  proxyWindowName,
  proxyDocumentName,
  configJson,
}) => {
  const proxiesForHead = {
    appendChild: {
      get(node, callback) {
        return proxyAppendChild(node, callback, head, proxyWindowName, proxyDocumentName);
      },
    },
  };
  const headProxy = ProxyHead(proxiesForHead, head, microfrontendName);

  const proxyDocument = ProxyDocument(
    {
      querySelector: {
        get(prop) {
          /**
           * @see https://github.com/cssinjs/jss/blob/master/packages/jss/src/DomRenderer.js#L127
           */
          if (prop === 'head') {
            return headProxy;
          }
          //Path for code like these, in FontFace.js inside UI5-webcomponent-base
          //Only handle these "head>style"
          /**
           *  if (!document.querySelector(`head>style[data-ui5-font-face]`)) {
                createStyleInHead(fontFaceCSS, { "data-ui5-font-face": "" });
              }
           */
          if (
            typeof prop === 'string' &&
            prop !== '' &&
            prop.replace(/ /gi, '').startsWith('head>')
          ) {
            let newProp = prop.trim();
            newProp = newProp.slice(4).trim();
            if (newProp.startsWith('>')) {
              newProp = newProp.slice(1).trim();
            }
            return headProxy.querySelector(newProp);
          }
          return document.querySelector(prop);
        },
      },
      createTreeWalker: {
        get(node) {
          if (node instanceof Document) {
            //If we use Proxy, assume it's a document
            return document.createTreeWalker(document);
          } else {
            return document.createTreeWalker(node);
          }
        },
      },
    },
    headProxy,
    null,
    microfrontendName,
    configJson,
  );
  const proxySandbox = new ProxySandbox(
    `${microfrontendHost}-${microfrontendName}-proxy-window`,
    proxyDocument,
    microfrontendName,
    configJson,
  );
  const proxyWindow = proxySandbox.proxy;
  return {
    proxyWindow,
    proxyDocument,
  };
};

export const createShadowRootFromContainer = domContainer => {
  let shadow;
  if (domContainer.attachShadow) {
    shadow = domContainer.attachShadow({
      mode: 'open',
    });
  } else {
    // createShadowRoot was proposed in initial spec, which has then been deprecated
    shadow = domContainer.createShadowRoot();
  }
  return shadow;
};

export const createScriptElement = ({
  microfrontendHost,
  microfrontendName,
  mainBundleScript = '',
  mainBundleScriptMap = '',
  mainBundleScriptHash = '',
  proxyWindowName = null,
  proxyDocumentName = null,
  id,
}) => {
  const script = document.createElement('script');
  script.id = id || `${microfrontendName}-application-script-tag`;
  script.setAttribute(testIdProp, script.id);
  script.integrity = mainBundleScriptHash;
  script.innerHTML =
    `;(function(window, self, globalThis, document){with(window){` +
    `${mainBundleScript}\n}}).bind(` +
    `window.${proxyWindowName})(window.${proxyWindowName}, window.${proxyWindowName}, ` +
    `window.${proxyWindowName}, window.${proxyDocumentName});${mainBundleScriptMap}`;
  // `window.__proxy)(window.__proxy, window.__proxy, window.__proxy, window.__document)`
  return script;
};

export const createStyleElement = ({
  microfrontendHost,
  microfrontendName,
  mainBundleStyle = '',
  mainBundleStyleMap = '',
  mainBundleStyleHash = '',
  id = null,
}) => {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.id = id || `${microfrontendName}-application-style-tag`;
  styleSheet.setAttribute(testIdProp, styleSheet.id);
  styleSheet.innerHTML = `\n${mainBundleStyle || ''}\n${mainBundleStyleMap}\n`;
  styleSheet.integrity = mainBundleStyleHash;
  return styleSheet;
};

export const createMicrofrontend = ({
  microfrontendHost,
  microfrontendName,
  frameworkScript = '',
  frameworkStyle = '',
  mainBundleScript = '',
  mainBundleScriptMap = '',
  mainBundleScriptHash = '',
  mainBundleStyle = '',
  mainBundleStyleMap = '',
  mainBundleStyleHash = '',
  configJson = null,
}) => {
  if (isolationProxyMap[`${microfrontendName}Application`]) {
    //re-apply window and document proxies to global
    const proxies = isolationProxyMap[`${microfrontendName}Application`];
    const { proxyWindow, proxyWindowName, proxyDocument, proxyDocumentName } = proxies;
    window[proxyWindowName] = proxyWindow;
    window[proxyDocumentName] = proxyDocument;
    return isolationProxyMap[`${microfrontendName}Application`];
  }
  const microfrontendContainer = document.createElement('div');
  microfrontendContainer.id = `${microfrontendName}-application-container`;
  microfrontendContainer.setAttribute(testIdProp, `${microfrontendName}-application-container`);
  microfrontendContainer.style.width = '100%';
  microfrontendContainer.style.height = '100%';
  let shadow = null;
  if (configJson?.components?.length > 0) {
    const targetComponent = configJson?.components?.find(
      comp => comp?.config.app === microfrontendName,
    );
    const { config } = targetComponent;
    if (config.shadow === false) {
      shadow = microfrontendContainer;
    } else if (config.shadow === true) {
      shadow = createShadowRootFromContainer(microfrontendContainer);
    }
  }
  if (!shadow) {
    shadow = createShadowRootFromContainer(microfrontendContainer);
  }
  const mainBundleScriptRefined = `;${mainBundleScript}`.replace(
    '//# sourceMappingURL=main.bundle.js.map',
    '',
  );

  const microfrontendHead = document.createElement('aside');
  microfrontendHead.id = `${microfrontendName}-application-container-shadow-head`;
  microfrontendHead.setAttribute(
    testIdProp,
    `${microfrontendName}-application-container-shadow-head`,
  );

  const microfrontendBody = document.createElement('section');
  microfrontendBody.className = `${microfrontendName}-application-container-shadow-body`;
  microfrontendBody.id = `${microfrontendName}-application-container-shadow-body`;
  microfrontendBody.setAttribute(
    testIdProp,
    `${microfrontendName}-application-container-shadow-body`,
  );
  microfrontendBody.style.width = '100%';
  microfrontendBody.style.height = '100%';

  const proxyWindowName = `${getValidName(microfrontendName)}ApplicationWindow`;
  const proxyDocumentName = `${getValidName(microfrontendName)}ApplicationDocument`;

  const { proxyWindow, proxyDocument } = createProxyWindowAndDocument({
    microfrontendHost,
    microfrontendName,
    head: microfrontendHead,
    body: microfrontendBody,
    proxyWindowName,
    proxyDocumentName,
    configJson,
  });

  window[proxyWindowName] = proxyWindow;
  window[proxyDocumentName] = proxyDocument;

  const renderContainer = document.createElement('div');
  renderContainer.style.width = '100%';
  renderContainer.style.height = '100%';
  renderContainer.className = `${microfrontendName}-render-container`;

  shadow.appendChild(microfrontendHead);
  shadow.appendChild(microfrontendBody);
  isolationProxyMap[`${microfrontendName}Application`] = {
    microfrontendContainer,
    renderContainer,
    proxyWindow,
    proxyWindowName,
    proxyDocument,
    proxyDocumentName,
    configJson,
    initialize() {
      const mainBundleScriptMapRefined = `\n\n//# sourceMappingURL=${microfrontendHost}${mainBundleScriptMap}`;
      const mainBundleScriptTag = createScriptElement({
        microfrontendHost,
        microfrontendName,
        mainBundleScript: mainBundleScriptRefined,
        mainBundleScriptMap: mainBundleScriptMapRefined,
        mainBundleScriptHash,
        proxyWindowName,
        proxyDocumentName,
      });
      const mainBundleStyleRefined = mainBundleStyle;
      const mainBundleStyleMapRefined = mainBundleStyleMap;

      const mainBundleStyleTag = createStyleElement({
        microfrontendHost,
        microfrontendName,
        mainBundleStyle: mainBundleStyleRefined,
        mainBundleStyleMap: mainBundleStyleMapRefined,
        mainBundleStyleHash,
      });
      microfrontendHead.childNodes.forEach(childNode => {
        microfrontendHead.removeChild(childNode);
      });
      microfrontendBody.childNodes.forEach(childNode => {
        microfrontendBody.removeChild(childNode);
      });
      microfrontendHead.appendChild(mainBundleStyleTag);
      microfrontendBody.appendChild(renderContainer);
      microfrontendBody.appendChild(mainBundleScriptTag);
    },
  };
  return isolationProxyMap[`${microfrontendName}Application`];
};

function getRandom(digit = 5) {
  return (Math.random() + '').slice(2, digit + 2);
}

export const defaultGetManifestData = host => {
  return axios(`${host}/asset-manifest.json`, {
    crossDomain: true,
  });
};

export const registManifest = (name, manifest) => {
  manifestMap[name] = manifest;
};

export const registFragment = (host, name, configJson) => {
  return new Promise((resolve, reject) => {
    fetchManifest({ host, name, getManifest: null }).then(manifest => {
      registManifest(name, manifest);
      fetchFragmentManifestAssets({ manifest, host, name, configJson }).then(resolve, reject);
    });
  });
};

export const fetchManifest = ({ host, name, getManifest }) => {
  return new Promise((resolve, reject) => {
    const finalGetManifest = getManifest || defaultGetManifestData;
    if (manifestMap[name]) {
      resolve(manifestMap[name]);
    } else {
      finalGetManifest(host).then(result => {
        const manifest = result?.data || result;
        if (manifest) {
          registManifest(name, manifest);
          resolve(manifestMap[name]);
        } else {
          console.error(
            `Fetch data from ${host} ${name} with function ${
              getManifest || defaultGetManifestData
            } failed, result is ${result}`,
          );
          reject(
            new Error(
              `Fetch data from ${host} ${name} with function ${
                getManifest || defaultGetManifestData
              } failed, result is ${result}`,
            ),
          );
        }
      }, reject);
    }
  });
};

const getValidAssetsFromFragmentManifest = (manifest, host) => {
  let fragmentCssHash = '';
  let fragmentJsHash = '';

  if (manifest.hash && manifest.hash[fragmentCssKey]) {
    fragmentCssHash = manifest.hash[fragmentCssKey];
  }
  if (manifest.hash && manifest.hash[fragmentJsKey]) {
    fragmentJsHash = manifest.hash[fragmentJsKey];
  }

  const isFragmentCssExist = !!manifest.files[fragmentCssKey];
  const JSPath = manifest.files[fragmentJsKey] || '';
  const assets = [axios.get(JSPath.startsWith('http') ? JSPath : `${host}${JSPath}`)];

  let fragmentCssURL = manifest.files[fragmentCssKey];
  if (fragmentCssURL && !fragmentCssURL.trim().startsWith('http')) {
    fragmentCssURL = host + fragmentCssURL;
  }
  if (isFragmentCssExist) {
    assets.push(axios.get(fragmentCssURL));
  } else {
    assets.push(
      Promise.resolve({
        data: '/** No Style Data */',
      }),
    );
  }
  return { assets, fragmentJsHash, fragmentCssHash };
};

export const fetchFragmentManifestAssets = ({ manifest, host, name, configJson = null }) => {
  return new Promise((resolve, reject) => {
    if (isolationProxyMap[`${name}Fragment`]) {
      //re-apply window and document proxies to global
      const proxies = isolationProxyMap[`${name}Fragment`];
      const { proxyWindow, proxyWindowName, proxyDocument, proxyDocumentName } = proxies;
      window[proxyWindowName] = proxyWindow;
      window[proxyDocumentName] = proxyDocument;
      resolve(proxies);
    } else {
      const { assets, fragmentJsHash, fragmentCssHash } = getValidAssetsFromFragmentManifest(
        manifest,
        host,
      );
      Promise.all(assets).then(results => {
        resolve(
          createFragment({
            microfrontendHost: host,
            microfrontendName: name,
            fragmentBundleScript: results[0].data,
            fragmentBundleScriptMap: manifest.files[fragmentJsKey + '.map'],
            fragmentBundleStyle: results[1].data,
            fragmentBundleStyleMap: manifest.files[fragmentCssKey + '.map'],
            fragmentBundleScriptHash: fragmentJsHash,
            fragmentBundleStyleHash: fragmentCssHash,
            configJson,
          }),
        );
      }, reject);
    }
  });
};

export const createFragment = ({
  microfrontendHost,
  microfrontendName,
  fragmentBundleScript,
  fragmentBundleScriptMap,
  fragmentBundleScriptHash,
  fragmentBundleStyle,
  fragmentBundleStyleMap,
  fragmentBundleStyleHash,
  configJson = null,
}) => {
  if (isolationProxyMap[`${microfrontendName}Fragment`]) {
    //re-apply window and document proxies to global
    const proxies = isolationProxyMap[`${microfrontendName}Fragment`];
    const { proxyWindow, proxyWindowName, proxyDocument, proxyDocumentName } = proxies;
    window[proxyWindowName] = proxyWindow;
    window[proxyDocumentName] = proxyDocument;
    return isolationProxyMap[`${microfrontendName}Fragment`];
  }
  const fragmentStyle = createStyleElement({
    microfrontendHost,
    microfrontendName,
    mainBundleStyle: fragmentBundleStyle,
    mainBundleStyleMap: fragmentBundleStyleMap,
    mainBundleStyleHash: fragmentBundleStyleHash,
    id: `${microfrontendName}-fragment-style-${getRandom()}`,
  });
  const proxyWindowName = `${getValidName(microfrontendName)}ApplicationWindow`;
  const proxyDocumentName = `${getValidName(microfrontendName)}FragmentDocument`;

  const microfrontendHead = document.createElement('aside');
  microfrontendHead.id = `${microfrontendName}-fragment-container-shadow-head`;

  const microfrontendBody = document.createElement('section');
  microfrontendBody.className = `${microfrontendName}-fragment-container-shadow-body`;
  microfrontendBody.style.width = '100%';
  microfrontendBody.style.height = '100%';

  //TODO We only support one instance in use for one time.

  const { proxyWindow, proxyDocument } = createProxyWindowAndDocument({
    microfrontendHost,
    microfrontendName,
    head: microfrontendHead,
    body: microfrontendBody,
    proxyWindowName,
    proxyDocumentName,
    configJson,
  });

  window[proxyWindowName] = proxyWindow;
  window[proxyDocumentName] = proxyDocument;

  const fragmentBundleScriptMapRefined = `\n\n//# sourceMappingURL=${microfrontendHost}${fragmentBundleScriptMap}`;
  const fragmentScript = createScriptElement({
    microfrontendHost,
    microfrontendName,
    mainBundleScript: fragmentBundleScript,
    mainBundleScriptMap: fragmentBundleScriptMapRefined,
    mainBundleScriptHash: fragmentBundleScriptHash,
    proxyWindowName,
    proxyDocumentName,
    id: `${microfrontendName}-fragment-script-${getRandom()}`,
  });
  document.head.appendChild(fragmentScript);
  microfrontendHead.appendChild(fragmentStyle);
  isolationProxyMap[`${microfrontendName}Fragment`] = {
    configJson,
    microfrontendHead,
    microfrontendBody,
    fragmentStyle,
    fragmentScript,
    microfrontendHost,
    microfrontendName,
    fragmentBundleScript,
    fragmentBundleScriptMap,
    fragmentBundleScriptHash,
    fragmentBundleStyle,
    fragmentBundleStyleMap,
    fragmentBundleStyleHash,
    proxyWindowName,
    proxyWindow,
    proxyDocumentName,
    proxyDocument,
  };
  fragmentManager.registerMicrofrontendFragments({
    microfrontendName,
    regist: isolationProxyMap[`${microfrontendName}Fragment`],
  });
  return isolationProxyMap[`${microfrontendName}Fragment`];
};

const getAssetsFromApplicationManifest = (manifest, host) => {
  let mainCssHash = '';
  let mainJsHash = '';

  if (manifest.hash && manifest.hash[mainCssKey]) {
    mainCssHash = manifest.hash[mainCssKey];
  }
  if (manifest.hash && manifest.hash[mainJsKey]) {
    mainJsHash = manifest.hash[mainJsKey];
  }

  const isMainCssExist = !!manifest.files[mainCssKey];

  const mainJsUrl = manifest.files[mainJsKey]?.startsWith('http')
    ? manifest.files[mainJsKey]
    : host + manifest.files[mainJsKey];
  const mainCssUrl = manifest.files[mainCssKey]?.startsWith('http')
    ? manifest.files[mainCssKey]
    : host + manifest.files[mainCssKey];

  const assets = [axios.get(mainJsUrl)];

  if (isMainCssExist) {
    assets.push(axios.get(mainCssUrl));
  } else {
    assets.push(
      Promise.resolve({
        data: '/** No Style Data Loaded */',
      }),
    );
  }

  let scriptMap = manifest.files[mainJsKey + '.map'];
  if (!scriptMap) {
    // If main.js.map is null for manifest.json, means we will use full name of mainJS to locate map
    if (manifest.files[mainJsKey] && manifest.files[mainJsKey] !== '') {
      const index = mainJsUrl.lastIndexOf('/');
      if (index >= 0) {
        scriptMap = manifest[mainJsUrl.slice(index + 1) + '.map'];
      }
    }
  }
  return { assets, mainCssHash, mainJsHash, scriptMap };
};

export const getMainJsSourceMapURL = manifest => {
  const mainJsWithHash = manifest.files[mainJsKey];
  const mainJsFileName = mainJsWithHash.slice(mainJsWithHash.lastIndexOf('/') + 1);
  const originalURL = manifest.files[mainJsKey + '.map'];
  const newURL = manifest.files[mainJsFileName + '.map'];
  return originalURL || newURL;
};

export const getMainCssSourceMapURL = manifest => {
  const mainCssWithHash = manifest.files[mainCssKey];
  const mainCssFileName = mainCssWithHash?.slice(mainCssWithHash?.lastIndexOf('/') + 1);
  const originalURL = manifest.files[mainCssKey + '.map'];
  const newURL = manifest.files[mainCssFileName + '.map'];
  return originalURL || newURL;
};

export const fetchApplicationManifestAssets = (manifest, host, name, configJson = null) => {
  return new Promise((resolve, reject) => {
    if (isolationProxyMap[`${name}Application`]) {
      //re-apply window and document proxies to global
      const proxies = isolationProxyMap[`${name}Application`];
      const { proxyWindow, proxyWindowName, proxyDocument, proxyDocumentName } = proxies;
      window[proxyWindowName] = proxyWindow;
      window[proxyDocumentName] = proxyDocument;
      resolve(proxies);
    } else {
      const { mainCssHash, mainJsHash, assets } = getAssetsFromApplicationManifest(manifest, host);
      Promise.all(assets).then(results => {
        resolve();
        const finalAssets = {
          scriptData: results[0].data,
          scriptMap: getMainJsSourceMapURL(manifest),
          cssData: results[1].data,
          cssMap: getMainCssSourceMapURL(manifest),
          cssHash: mainCssHash,
          jsHash: mainJsHash,
        };
        const { scriptData, cssData, scriptMap, cssMap, jsHash, cssHash } = finalAssets;
        createMicrofrontend({
          microfrontendHost: host,
          microfrontendName: name,
          mainBundleScript: scriptData,
          mainBundleScriptMap: scriptMap,
          mainBundleStyle: cssData,
          mainBundleStyleMap: cssMap,
          mainBundleScriptHash: jsHash,
          mainBundleStyleHash: cssHash,
          configJson,
        });
      }, reject);
    }
  });
};

export const renderApplication = (
  microfrontendName,
  history,
  props,
  domContainer,
  configJson = null,
) => {
  const applicationRegisty = isolationProxyMap[`${microfrontendName}Application`];
  const { microfrontendContainer, renderContainer, initialize } = applicationRegisty;
  initialize();
  domContainer.removeChild(domContainer.children[0]);
  domContainer.appendChild(microfrontendContainer);
  AppManager.renderApp(`${microfrontendName.toLowerCase()}`, renderContainer, history, {
    ...props,
  });
};
export const unmountApplication = name => {
  const { renderContainer } = isolationProxyMap[`${name}Application`] || {};
  if (renderContainer) {
    AppManager.unmountApp(`${name.toLowerCase()}`, renderContainer);
  }
};
